/**
 * Ordered source of truth for the `capacityCondition` picklist. The array index is the
 * value carried in the URL (e.g. `?capacityCondition=in_0-2`) and consumed by both the
 * option builder (`useGetFilterOptions`) and the parser (`buildLoopTeamsFiltersFromSearch`).
 * Do NOT reorder — it would silently remap existing bookmarked/shared links.
 */
export const CAPACITY_CONDITIONS = [
  'capacityPressure',
  'underutilized',
  'headroomAvailable',
] as const;

export type CapacityCondition = (typeof CAPACITY_CONDITIONS)[number];

/**
 * All valid loop-team filter keys.
 *
 * `roleId`/`skillId` reuse the shared picklist filters; `capacityCondition`,
 * `openRoleId` and `openSkillId` are contributed by this feature (see
 * LOOP_TEAM_PICKLIST_FILTERS). `openRoleId`/`openSkillId` ("teams needing a
 * role/skill") are applied via the workforce-mix "Teams needing" link and
 * rendered as clearable chips, but are intentionally excluded from the
 * "Filter by" menu — hence their absence from LOOP_TEAM_MENU_FILTER_KEYS.
 */
export const LOOP_TEAM_FILTER_KEYS = [
  'roleId',
  'skillId',
  'capacityCondition',
  'openRoleId',
  'openSkillId',
] as const;

export type LoopTeamFilterKey = (typeof LOOP_TEAM_FILTER_KEYS)[number];

/** Picklist keys this feature contributes to the shared compact-filter registry. */
export const LOOP_TEAM_PICKLIST_FILTERS = [
  'capacityCondition',
  'openRoleId',
  'openSkillId',
] as const satisfies readonly LoopTeamFilterKey[];

/** Keys applied only via workforce-mix "Teams needing" links, never from the menu. */
export const LOOP_TEAM_OPEN_POSITION_FILTER_KEYS = [
  'openRoleId',
  'openSkillId',
] as const satisfies readonly LoopTeamFilterKey[];

/** Categories offered in the "Filter by" menu (excludes the link-only open-position keys). */
export const LOOP_TEAM_MENU_FILTER_KEYS = [
  'roleId',
  'skillId',
  'capacityCondition',
] as const satisfies readonly LoopTeamFilterKey[];

export const getLoopTeamTableFilters = () => [...LOOP_TEAM_MENU_FILTER_KEYS] as const;

export const getLoopTeamFilterKeys = () => [...LOOP_TEAM_FILTER_KEYS] as const;
